import styles from './LinkedWalletsCard.module.css';

import type { FC } from 'react';
import type { ChainFamily } from 'src/blockchain';

import * as Sentry from '@sentry/react';
import React, { useId, useState } from 'react';
import { CheckCircle } from '@phosphor-icons/react';

import { useUserSession } from 'src/hooks/useUserSession';
import { useWalletSecondFactor, WalletNotConnectedError } from 'src/hooks/useWalletSecondFactor';

import { isTelegramWebApp } from 'src/utils/telegram';
import { linkableFamilies } from 'src/utils/linkableFamilies';
import { shortWalletAddress, securityErrorMessage } from 'src/utils/securityError';

import { useLinkWallet, useBuyerChains } from 'src/queries';
import { WalletButton, FAMILY_LABEL, familyActionName } from 'src/blockchain';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

/**
 * The account's wallet slots — one per wallet family this build carries (multichain spec §4.4).
 *
 * An account holds at most one wallet per family, and one EVM address serves every EVM chain, so
 * the slots are families, not chains: linking one EVM wallet is what makes Base — and later
 * Ethereum — payable, without touching the TRON wallet that is already linked.
 *
 * This is not cosmetic. Buy Now moves money client-side and records the purchase afterwards, so a
 * buyer who is offered a network of a family they never linked would pay the seller and only then
 * be told the purchase cannot be recorded. The network selector hides such a network; this card is
 * where the buyer fixes that.
 *
 * Needs the account session — the slots belong to the account, and since the e-mail-only sign-in
 * (api#402) this card is where a wallet enters the storefront at all: nothing signs in with one.
 *
 * A slot is offered for a family the api takes new money in, and kept for one the account already
 * holds (`linkableFamilies`): the build still carries TRON, but no new purchase settles there
 * (api#401), so an account without a TRON wallet is not invited to link one.
 */
export const LinkedWalletsCard: FC = () => {
  const { isLoggedIn, account } = useUserSession();
  // Asked only for a signed-in account: the card shows nothing to anyone else.
  const { data: buyerChains } = useBuyerChains(isLoggedIn && !!account);

  if (!isLoggedIn || !account) {
    return null;
  }

  const linkedFamilies = account.wallets?.map((wallet) => wallet.chainType) ?? [];

  return (
    <Stack gap={3} className={styles.card}>
      <Stack gap={1}>
        <Typography color="white" fontWeight="600">
          Linked wallets
        </Typography>
        <Typography color="lightWhite" variant="small">
          One wallet per network family. Linking a wallet is what lets you pay on the networks of
          that family.
        </Typography>
      </Stack>

      {/* Telegram's built-in browser opens no wallet extension: the slots are shown, since which
          wallets work there depends on the chain the app was built for, but the way out is named —
          the same hint the e-mail change card gives. */}
      {isTelegramWebApp() ? (
        <Typography color="lightWhite" variant="small">
          If your wallet can&apos;t connect here, open GAC in Chrome or Safari — Telegram&apos;s
          built-in browser blocks most wallets.
        </Typography>
      ) : null}

      {/* Until the api has answered — or when it fails — only the wallets the account holds. */}
      {linkableFamilies({ buyerChains: buyerChains ?? [], linkedFamilies }).map((family) => (
        <WalletSlot
          key={family}
          family={family}
          linkedAddress={
            account.wallets?.find((wallet) => wallet.chainType === family)?.walletAddress
          }
        />
      ))}
    </Stack>
  );
};

const WalletSlot: FC<{ family: ChainFamily; linkedAddress?: string }> = ({
  family,
  linkedAddress,
}) => {
  const labelId = useId();
  // A challenge is one-time since api#131, so it is fetched per attempt inside `sign` rather than
  // held in a query cache. The family travels with it: an EVM signature must be verified by the
  // EVM verifier, and a challenge issued for one family is refused for the other.
  const { sign, canSign } = useWalletSecondFactor(family);
  const linkMutation = useLinkWallet();

  const [linking, setLinking] = useState(false);
  const [linked, setLinked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLink = async () => {
    if (!canSign || linking) return;

    setLinking(true);
    setError(null);

    try {
      await linkMutation.mutateAsync(await sign('auth'));
      setLinked(true);
    } catch (err) {
      if (err instanceof WalletNotConnectedError) {
        setError(err.message);
        return;
      }
      Sentry.captureException(err, { tags: { action: 'user_wallet_link', family } });
      setError(securityErrorMessage(err, 'Could not link the wallet. Please try again.'));
    } finally {
      setLinking(false);
    }
  };

  // Each slot is a group named by its family, in every state: an empty account shows two empty
  // slots, and with both wallets connected two `Link wallet` buttons — the first one pressed makes
  // its wallet the account's primary for good. The action's name says the family after its
  // visible title (`familyActionName`), so speech input that says what it sees still finds it.
  const heading = (
    <Typography id={labelId} color="lightWhite" variant="small">
      {FAMILY_LABEL[family]}
    </Typography>
  );

  if (linkedAddress) {
    return (
      <Stack gap={1} className={styles.slot} role="group" aria-labelledby={labelId}>
        {heading}
        <Stack flexDirection="row" gap={2} alignItems="center">
          <CheckCircle size={18} color="#CCFF00" weight="fill" />
          <Typography color="white" variant="small" className={styles.mono}>
            {shortWalletAddress(linkedAddress)}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  // Freshly linked: `/user/auth/me` is refetched by the mutation, but say so immediately rather
  // than letting the slot sit in its "not linked" state until the refetch lands.
  if (linked) {
    return (
      <Stack gap={1} className={styles.slot} role="group" aria-labelledby={labelId}>
        {heading}
        <Stack flexDirection="row" gap={2} alignItems="center">
          <CheckCircle size={18} color="#CCFF00" weight="fill" />
          <Typography color="white" variant="small">
            Wallet linked to your account.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const title = linking ? 'Signing...' : 'Link wallet';

  return (
    <Stack gap={2} className={styles.slot} role="group" aria-labelledby={labelId}>
      <Stack gap={1}>
        {heading}
        <Typography color="white" variant="small">
          Not linked
        </Typography>
      </Stack>
      <WalletButton family={family} />
      {canSign ? (
        <Button
          title={title}
          aria-label={familyActionName(title, family)}
          variant="white"
          onClick={handleLink}
          disabled={linking}
        />
      ) : null}
      {error ? (
        <Typography color="danger" variant="small">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};
